import React, { useState } from 'react';

interface AdminQuickActionsProps {
  apiBaseUrl: string;
  adminKey: string;
  onBroadcast: () => void;
  onToggleBots: () => void;
  onResetEconomy: () => void;
  onReloadCollisions: () => void;
  onFullResetWorld?: () => void;
  onViewPlayers: () => void;
  onViewBotActivity: () => void;
  onResetCharacter?: (playerId: string) => void;
  isLoading?: boolean;
}

export const AdminQuickActions: React.FC<AdminQuickActionsProps> = ({
  apiBaseUrl,
  adminKey,
  onBroadcast,
  onToggleBots,
  onResetEconomy,
  onReloadCollisions,
  onFullResetWorld,
  onViewPlayers,
  onViewBotActivity,
  onResetCharacter,
  isLoading = false,
}) => {
  const [botsEnabled] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');

  const quickActions = [
    {
      id: 'broadcast',
      label: 'Broadcast',
      description: 'Send message to all players',
      onClick: onBroadcast,
      color: '#3b82f6',
    },
    {
      id: 'toggle-bots',
      label: botsEnabled ? 'Pause Bots' : 'Resume Bots',
      description: botsEnabled ? 'Stop bot trading' : 'Resume bot trading',
      onClick: onToggleBots,
      color: botsEnabled ? '#f59e0b' : '#22c55e',
    },
    {
      id: 'reset-economy',
      label: 'Reset Economy',
      description: 'Reset all currency',
      onClick: onResetEconomy,
      color: '#ef4444',
    },
    ...(onFullResetWorld
      ? [
          {
            id: 'full-reset-world',
            label: 'Full World Reset',
            description: 'Regenerate all islands/chunks',
            onClick: onFullResetWorld,
            color: '#b91c1c',
          },
        ]
      : []),
    {
      id: 'reload-collisions',
      label: 'Reload Collisions',
      description: 'Reload collision cache',
      onClick: onReloadCollisions,
      color: '#8b5cf6',
    },
    {
      id: 'view-players',
      label: 'Live Players',
      description: 'View online players',
      onClick: onViewPlayers,
      color: '#06b6d4',
    },
    {
      id: 'view-bot-activity',
      label: 'Bot Activity',
      description: 'View bot activity feed',
      onClick: onViewBotActivity,
      color: '#22c55e',
    },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        top: 20,
        width: expanded ? 280 : 60,
        backgroundColor: '#1a1a2e',
        border: '2px solid #4a5568',
        borderRadius: 0,
        padding: expanded ? 16 : 12,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        transition: 'width 0.3s ease',
        fontFamily: 'monospace',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: expanded ? 12 : 0,
        }}
      >
        {expanded && (
          <h3 style={{ color: '#ffa500', fontSize: 13, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>QUICK ACTIONS</h3>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#a0aec0',
            fontSize: 16,
            cursor: 'pointer',
            padding: 0,
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '▼' : '▲'}
        </button>
      </div>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={isLoading}
              style={{
                padding: '10px 12px',
                backgroundColor: isLoading ? '#2d3748' : action.color,
                color: '#000',
                border: 'none',
                borderRadius: 0,
                fontSize: 12,
                fontWeight: 600,
                cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                transition: 'opacity 0.2s',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
              title={action.description}
            >
              <span>{action.label}</span>
              <span style={{ fontSize: 10, opacity: 0.8 }}>{action.description}</span>
            </button>
          ))}
          
          {/* Reset Character Section */}
          <div style={{ borderTop: '1px solid #4a5568', paddingTop: 8, marginTop: 8 }}>
            <div style={{ fontSize: 10, color: '#a0aec0', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Reset Character</div>
            <input
              type="text"
              placeholder="Player ID"
              value={selectedPlayerId}
              onChange={(e) => setSelectedPlayerId(e.target.value)}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: '#2d3748',
                color: '#edf2f7',
                border: '1px solid #4a5568',
                borderRadius: 0,
                fontSize: 11,
                fontFamily: 'monospace',
                marginBottom: 6,
              }}
            />
            <button
              onClick={() => {
                if (selectedPlayerId && onResetCharacter) {
                  onResetCharacter(selectedPlayerId);
                  setSelectedPlayerId('');
                }
              }}
              disabled={isLoading || !selectedPlayerId}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: isLoading || !selectedPlayerId ? '#2d3748' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 0,
                fontSize: 12,
                fontWeight: 600,
                cursor: isLoading || !selectedPlayerId ? 'default' : 'pointer',
                opacity: isLoading || !selectedPlayerId ? 0.6 : 1,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
              title="Reset character to core world"
            >
              Reset to Core
            </button>
          </div>
        </div>
      )}

      {!expanded && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            alignItems: 'center',
          }}
        >
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={isLoading}
              style={{
                padding: '6px',
                backgroundColor: isLoading ? '#2d3748' : action.color,
                color: '#000',
                border: 'none',
                borderRadius: 0,
                fontSize: 14,
                cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={action.description}
            >
              {action.label.split(' ')[0]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
